import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

export type ColorMode = "light" | "dark";

const STORAGE_KEY = "tih-color-mode";

type ColorModeContextValue = {
  mode: ColorMode;
  setMode: (mode: ColorMode) => void;
  toggleMode: () => void;
};

const ColorModeCtx = createContext<ColorModeContextValue | null>(null);

function readStoredMode(): ColorMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark") return raw;
  } catch {
    /* private mode */
  }
  return "dark";
}

function applyColorMode(mode: ColorMode) {
  const root = document.documentElement;
  root.setAttribute("data-color-mode", mode);
  root.style.colorScheme = mode;
}

export function ColorModeProvider({ children }: PropsWithChildren) {
  const [mode, setModeState] = useState<ColorMode>(() =>
    typeof window === "undefined" ? "dark" : readStoredMode(),
  );

  useEffect(() => {
    applyColorMode(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  const setMode = useCallback((next: ColorMode) => {
    setModeState(next);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((m) => (m === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({ mode, setMode, toggleMode }),
    [mode, setMode, toggleMode],
  );

  return <ColorModeCtx.Provider value={value}>{children}</ColorModeCtx.Provider>;
}

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeCtx);
  if (!ctx) throw new Error("useColorMode must be used within ColorModeProvider");
  return ctx;
}
